import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useStory, STEPS } from '../../context/StoryContext';
import { api } from '../../services/api';
import { Send } from 'lucide-react';
import { Checkbox } from '../ui/checkbox';
import BackArrow from '../ui/BackArrow';

const TURRIS_LOGO = '/logo-turris.svg';

export default function FormScreen() {
  const { 
    goToStep, goBack, language, protagonistName, protagonistType, uiTexts, 
    setLeadId, setPdfUrl, setIsLoading, isLoading, setUserEmail 
  } = useStory();
  
  const [email, setEmail] = useState('');
  const [emailConfirm, setEmailConfirm] = useState('');
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [marketingAccepted, setMarketingAccepted] = useState(false);
  const [errors, setErrors] = useState({});
  const [config, setConfig] = useState({ newsletter_required: true });

  const t = uiTexts || {};

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const appConfig = await api.getConfig();
        setConfig(appConfig);
      } catch (e) {
        console.error('Error loading config:', e);
      }
    };
    loadConfig();
    api.trackEvent('form_started');
  }, []);

  const validateForm = () => {
    const newErrors = {};
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) {
      newErrors.email = language === 'ca' ? 'Introdueix un email vàlid' : 'Introduce un email válido';
    }
    
    // Email confirmation - validate only if first email is valid
    if (!newErrors.email && email !== emailConfirm) {
      newErrors.emailConfirm = t.form_email_error_match || 'Els emails no coincideixen';
    }
    
    // Privacy acceptance
    if (!privacyAccepted) {
      newErrors.privacy = language === 'ca' ? 'Has d\'acceptar la política de privacitat' : 'Debes aceptar la política de privacidad';
    }
    
    // Marketing acceptance (if required)
    if (config.newsletter_required && !marketingAccepted) {
      newErrors.marketing = language === 'ca' ? 'Has d\'acceptar rebre comunicacions' : 'Debes aceptar recibir comunicaciones';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsLoading(true);
    
    try {
      // Create lead
      const lead = await api.createLead({
        language,
        protagonistName,
        protagonistType,
        email,
        privacyAccepted,
        marketingAccepted
      });
      
      setLeadId(lead.id);
      setUserEmail(email); // Save email to context for SuccessScreen
      
      // PDF generation and email sending is now handled by SuccessScreen
      // using the new booklet format
      
      api.trackEvent('form_completed', { language, protagonist_name: protagonistName });
      
      goToStep(STEPS.SUCCESS);
    } catch (error) {
      console.error('Error submitting form:', error);
      setErrors({ submit: 'Error al procesar. Por favor, inténtalo de nuevo.' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#FDFBF7' }}>
      {/* Header */}
      <header className="p-6 flex items-center justify-between">
        <BackArrow onClick={goBack} />
        <img src={TURRIS_LOGO} alt="Turris" className="h-8" />
        <div className="w-10" />
      </header>

      {/* Progress indicator */}
      <div className="flex justify-center gap-2 py-4">
        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#2A9D8F' }} />
        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#2A9D8F' }} />
        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#2A9D8F' }} />
        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#2A9D8F' }} />
        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#D32F2F' }} />
      </div>

      {/* Main content */}
      <main className="flex-1 flex flex-col items-center px-6 pb-12">
        <motion.h1
          className="font-heading text-2xl sm:text-3xl font-bold text-center mb-2"
          style={{ color: '#2B2B2B' }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {t.form_title || 'Recibe tu cuento'}
        </motion.h1>
        
        <motion.p
          className="text-sm text-center mb-8 max-w-md"
          style={{ color: '#5C5C5C' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
        >
          {t.form_subtitle || 'Introduce tu email para recibir el cuento y poder descargarlo'}
        </motion.p>

        <motion.form
          onSubmit={handleSubmit}
          className="w-full max-w-md space-y-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          {/* Email field */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#2B2B2B' }}>
              {t.form_email || 'Email'}
            </label>
            <input
              data-testid="email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border-2 transition-colors"
              style={{ 
                borderColor: errors.email ? '#D32F2F' : '#F6F0E4',
                backgroundColor: 'white'
              }}
              placeholder="tu@email.com"
              disabled={isLoading}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
            />
            {errors.email && (
              <p className="text-sm mt-1" style={{ color: '#D32F2F' }}>{errors.email}</p>
            )}
          </div>

          {/* Email confirmation - no autocomplete, no copy/paste */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#2B2B2B' }}>
              {t.form_email_confirm || 'Repetir email'}
            </label>
            <input
              data-testid="email-confirm-input"
              type="email"
              value={emailConfirm}
              onChange={(e) => setEmailConfirm(e.target.value)}
              onPaste={(e) => e.preventDefault()}
              onCopy={(e) => e.preventDefault()}
              onCut={(e) => e.preventDefault()}
              onDrop={(e) => e.preventDefault()}
              className="w-full px-4 py-3 rounded-xl border-2 transition-colors"
              style={{ 
                borderColor: errors.emailConfirm ? '#D32F2F' : '#F6F0E4',
                backgroundColor: 'white'
              }}
              placeholder="tu@email.com"
              disabled={isLoading}
              autoComplete="new-password"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
            />
            {errors.emailConfirm && (
              <p className="text-sm mt-1" style={{ color: '#D32F2F' }}>{errors.emailConfirm}</p>
            )}
          </div>

          {/* Privacy checkbox */}
          <div className="flex items-start gap-3">
            <Checkbox
              data-testid="privacy-checkbox"
              id="privacy"
              checked={privacyAccepted}
              onCheckedChange={setPrivacyAccepted}
              disabled={isLoading}
              className="mt-1"
            />
            <label htmlFor="privacy" className="text-sm" style={{ color: '#2B2B2B' }}>
              He llegit i accepto la{' '}
              <Link 
                to="/politica-privacitat"
                className="underline font-medium"
                style={{ color: '#D32F2F' }}
              >
                política de privacitat
              </Link>
              {' '}i les{' '}
              <Link 
                to="/condicions-us"
                className="underline font-medium"
                style={{ color: '#D32F2F' }}
              >
                condicions d'ús
              </Link>
            </label>
          </div>
          {errors.privacy && (
            <p className="text-sm -mt-4" style={{ color: '#D32F2F' }}>{errors.privacy}</p>
          )}

          {/* Marketing checkbox */}
          <div className="flex items-start gap-3">
            <Checkbox
              data-testid="marketing-checkbox"
              id="marketing"
              checked={marketingAccepted}
              onCheckedChange={setMarketingAccepted}
              disabled={isLoading}
              className="mt-1"
            />
            <label htmlFor="marketing" className="text-sm" style={{ color: '#2B2B2B' }}>
              Accepto rebre comunicacions comercials i novetats de Turris
              <span style={{ color: '#D32F2F' }}> *</span>
            </label>
          </div>
          {errors.marketing && (
            <p className="text-sm -mt-4" style={{ color: '#D32F2F' }}>{errors.marketing}</p>
          )}

          {/* Submit error */}
          {errors.submit && (
            <div className="p-4 rounded-xl" style={{ backgroundColor: '#FEF5F5' }}>
              <p className="text-sm text-center" style={{ color: '#D32F2F' }}>{errors.submit}</p>
            </div>
          )}

          {/* Submit button */}
          <button
            data-testid="submit-button"
            type="submit"
            disabled={isLoading}
            className="w-full py-4 rounded-full text-lg font-semibold text-white shadow-lg flex items-center justify-center gap-2 transition-all duration-300 hover:-translate-y-1 disabled:opacity-50 disabled:hover:translate-y-0"
            style={{ 
              backgroundColor: '#D32F2F',
              boxShadow: '0 8px 24px rgba(211, 47, 47, 0.25)'
            }}
          >
            {isLoading ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                {t.loading || 'Preparando tu cuento mágico...'}
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                {t.form_submit || 'Enviar y descargar'}
              </>
            )}
          </button>
        </motion.form>
      </main>
    </div>
  );
}
